import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getOddsFormatIs } from '@cx-web/domain-types';
import { CookieService } from '@cx-web/feature-cookies';
import { useGeolocationContext } from '@cx-web/feature-geolocation/context';
import { useRepubContext } from '@cx-web/feature-repub';
import { useSiteConfig } from '@cx-web/feature-site-config';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS, trackingActions } from '@cx-web/feature-tracking';

import { useBetslipUtils } from './hooks/use-betslip-utils';
import { calculateBookieOddsAndSelectedBookieOdds } from './utils/betslip-utils';
import { clickout } from './clickout';
import LocalStorageService from './localstorage-actions';
import {
  type Bet,
  type BetslipProviderData,
  type BetslipProviderProps,
  type BetslipState,
  type BetTrackingData,
  type BetTrackingDataBetslipBets,
  type BetTrackingDataGeneric,
  type BetTrackingDataValues,
  type BookieOdds,
  type CallbackFn,
  type OddsFormatIs,
} from './types';

const BETSLIPDATA = 'betslip';
const ONE_DAY = 86_400_000; // milliseconds in a day

export type LastFetchStatus = { at: number; ok: boolean; reason?: string };

const isCxDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('cxDebug');
    if (fromQuery && fromQuery !== '0' && fromQuery !== 'false') return true;
    return document.cookie.split(';').some(c => c.trim().startsWith('cxDebug=1'));
  } catch {
    return false;
  }
};

export const BetslipContext = createContext<BetslipProviderData>(undefined as any as BetslipProviderData);
BetslipContext.displayName = 'BetslipContext';

export const BetslipProvider = ({
  children,
  onBetslipOpenChange,
  persistedBetslipData,
  userOddsFormat,
  ...props
}: PropsWithChildren<BetslipProviderProps>) => {
  const { repub, repubIs } = useRepubContext();

  // Held in a ref so the open/close callbacks below keep stable identities and dependency arrays,
  // matching the previous static import — callers don't need to memoize onBetslipOpenChange.
  const onBetslipOpenChangeRef = useRef(onBetslipOpenChange);
  useEffect(() => {
    onBetslipOpenChangeRef.current = onBetslipOpenChange;
  }, [onBetslipOpenChange]);

  const { countryCode, state } = useGeolocationContext();
  const { bookmakers } = useSiteConfig();

  const cookieService = useMemo(() => new CookieService(), []);
  const localStorageService = useMemo(() => new LocalStorageService(), []);

  const oddsFormatIs: OddsFormatIs = useMemo(() => getOddsFormatIs(userOddsFormat), [userOddsFormat]);

  // NOTE: betslipUtils encompasses helpers which do not mutate local state
  const betslipUtils = useBetslipUtils();

  const [added, setAdded] = useState<boolean>(false);
  const [betslip, setBetslip] = useState<Bet[]>(() => persistedBetslipData?.betslip || []);
  const [bookieOdds, setBookieOdds] = useState<BookieOdds[]>(() => persistedBetslipData?.bookieOdds || []);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedBookie, setSelectedBookie] = useState<BookieOdds | null>(
    () => persistedBetslipData?.selectedBookie || null,
  );
  const [stake, setStake] = useState<number>(() => Number(persistedBetslipData?.stake || 10));

  // Ensure we sync betslip cookies
  useEffect(() => {
    if (!betslip.length) {
      cookieService.select('betslipBookie').set();
      cookieService.select('betslipStake').set();
      return;
    }

    cookieService.select('betslipBookie').set(selectedBookie?.bookmakerCode, new Date().getTime() + ONE_DAY);
    cookieService.select('betslipStake').set(String(stake), new Date().getTime() + ONE_DAY);
  }, [cookieService, betslip, selectedBookie, stake]);

  const callbacksRef = useRef<Record<string, CallbackFn[]>>({});
  const hasFetchedBetslipDataRef = useRef<boolean>(false);
  const shouldResetBetslipOnCloseRef = useRef<boolean>(false);
  const lastFetchStatusRef = useRef<LastFetchStatus | null>(null);

  const betslipState = useMemo<BetslipState>(
    () => ({
      added,
      betslip,
      betIds: betslip.map(b => b.betId),
      bookieOdds,
      count: betslip.length,
      loading,
      open,
      selectedBookie,
      stake,
    }),
    [added, betslip, bookieOdds, loading, open, selectedBookie, stake],
  );

  const toggleAdded = useCallback((val?: boolean): void => {
    if (typeof val !== 'undefined') {
      setAdded(val);
      return;
    }

    setAdded(o => !o);
  }, []);

  const setBetslipOpen = useCallback(
    (newOpenState: boolean) => {
      if (repubIs.GZ) onBetslipOpenChangeRef.current?.(newOpenState);

      setOpen(newOpenState);
    },
    [repubIs.GZ],
  );

  const toggleBetslip = useCallback(
    (val?: boolean): void => {
      // Delegate to setBetslipOpen for both paths so the GZ scroll callback fires
      // exactly once — never inside a setState updater, which React may re-invoke.
      setBetslipOpen(typeof val !== 'undefined' ? val : !open);
    },
    [open, setBetslipOpen],
  );

  const changeStake = useCallback(
    (stake: number): void => {
      setStake(stake);
      cookieService.select('betslipStake').set(String(stake), new Date().getTime() + ONE_DAY);
    },
    [cookieService],
  );

  const syncModulesBetslipData = useCallback(() => {
    if (!betslip.length) return;
    callbacksRef.current.syncBetslip?.forEach(callback => callback(betslip, true));
  }, [betslip]);

  // NOTE: it is essential we pass betslip data here
  // as this helper is used to initialise local state
  const storeBetslipData = useCallback(
    (data: Bet[], shouldSyncModulesBetslip?: boolean): void => {
      const { bookieOdds: newBookieOdds = [], selectedBookie: newSelectedBookie = null } =
        calculateBookieOddsAndSelectedBookieOdds(data, selectedBookie?.bookmakerCode);

      // short helper
      if (!newBookieOdds.length) {
        setBetslip([]);
        return;
      }

      // assign state
      setBetslip(data);
      setBookieOdds(newBookieOdds);
      setSelectedBookie(newSelectedBookie);

      // handle callbacks
      if (shouldSyncModulesBetslip) callbacksRef.current.syncBetslip?.forEach(callback => callback(data, true));
    },
    [selectedBookie],
  );

  const fetchBetslipData = useCallback(
    (shouldSyncModulesBetslip?: boolean): void => {
      setLoading(true);

      const { getBetslipAPI, getBetslipCookieArray } = betslipUtils;

      const betslipCookieArray = getBetslipCookieArray();

      const numberOfBets = betslipCookieArray.length;
      const betslipHasMultipleBets = numberOfBets > 1;
      const betslipHasSingleBet = numberOfBets === 1;

      if (betslipHasMultipleBets || betslipHasSingleBet) {
        const betIds = betslipCookieArray.map(bet => parseInt(bet[0], 10));
        getBetslipAPI(betIds, countryCode, state, (status, res) => {
          lastFetchStatusRef.current = {
            ok: !!status?.success,
            reason: status?.success ? undefined : (res?.message ?? 'unknown'),
            at: Date.now(),
          };
          if (status.success && res.data[BETSLIPDATA]) {
            storeBetslipData(res.data[BETSLIPDATA].bets, shouldSyncModulesBetslip);
          }
          setLoading(false);
        });
      } else {
        lastFetchStatusRef.current = { ok: true, at: Date.now() };
        setBetslip([]);
        setBookieOdds([]);
        setSelectedBookie(null);
        setLoading(false);
      }

      setAdded(false);
      hasFetchedBetslipDataRef.current = true;
    },
    [betslipUtils, countryCode, state, storeBetslipData],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      // NOTE: server betslip initialisation is
      // supported on Atlas *only* as OC still
      // utilises legacy local storage API
      if (hasFetchedBetslipDataRef.current || !repubIs.OC) return;
      hasFetchedBetslipDataRef.current = true;
      fetchBetslipData();
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [fetchBetslipData, repubIs]);

  const addOrRemoveFromBetslip = useCallback(
    (
      betArray: any[],
      matchBetIds?: any[],
      bookie?: string,
      betOdds?: string,
      betName?: string,
      betLocation?: string,
      subeventName?: string,
      marketName?: string,
      eventName?: string,
    ): void => {
      setLoading(true);

      const { getBetslipCookieArray, getLocalStorageBetslip, postOddsClick, trackBetSlip } = betslipUtils;
      const cookieBetIdArray = getBetslipCookieArray();
      const localStorageBetslip = getLocalStorageBetslip();

      let betIndex: number;
      const trackingObject = {
        event: TRACKING_EVENTS.ADD_TO_BETSLIP,
        eventCategory: TRACKING_CATEGORIES.BETTING,
        eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.ADD_TO_BETSLIP],
        eventLabel: bookie,
        bookieCode: bookie,
        betOdds,
        betName,
        betLocation,
        subeventName,
        marketName,
        eventName,
      };

      for (let i = 0; i < betArray.length; i++) {
        // Add or remove the ocBetId arg depending on its presence in the betIdArray.
        betIndex = cookieBetIdArray.map(item => item[0]).indexOf(betArray[i].betId.toString());

        if (betIndex === -1) {
          setAdded(true);

          if (bookie) postOddsClick(betArray[i].betId, bookie, countryCode, state);

          trackingObject.eventLabel = bookie;
          // Push the bet into the array with a 1 for "selected".
          cookieBetIdArray.push([betArray[i].betId.toString(), '1']);
          // Add selectedBookie so it is retrievable via getBetSelectedBookieFromLocalStore()
          betArray[i].selectedBookie = bookie;
          localStorageBetslip[betArray[i].betId] = betArray[i];

          if (typeof matchBetIds !== 'undefined') {
            // eslint-disable-next-line array-callback-return
            matchBetIds.map(matchBet => {
              if (matchBet !== betArray[i].betId) {
                const removedBetIndex = cookieBetIdArray.map(item => item[0]).indexOf(matchBet.toString());
                if (removedBetIndex !== -1) {
                  cookieBetIdArray.splice(removedBetIndex, 1);
                  // Remove from localStorage
                  delete localStorageBetslip[matchBet];
                }
              }
            });
          }

          if (cookieBetIdArray.length === 1) {
            setBetslipOpen(true);
            // view betslip tracking when opening the betslip with first bet selection.
            trackBetSlip(
              [
                {
                  ...betArray,
                  marketName: marketName || '',
                  subeventName: subeventName || '',
                  name: betName || '',
                  bestOddsDecimal: Number(!oddsFormatIs.American && betOdds),
                  bestOddsUs: Number(oddsFormatIs.American && betOdds),
                  bestOddsBookmakers: bookie || '',
                },
              ],
              stake,
            );
          }
        } else {
          trackingObject.event = TRACKING_EVENTS.REMOVE_BET;
          trackingObject.eventAction = TRACKING_ACTIONS[TRACKING_EVENTS.REMOVE_BET];
          trackingObject.eventLabel = '';
          // Remove from cookie
          cookieBetIdArray.splice(betIndex, 1);

          // Remove from localStorage
          delete localStorageBetslip[betArray[i].betId];
        }
        trackingActions.eventTracking(trackingObject);
      }

      if (cookieBetIdArray.length === 0) {
        setBetslipOpen(false);
      }

      // Store mini betslip in localStorage to make odds avaialbe for estimated betslip odds.
      localStorageService.select('intBetslip').set(localStorageBetslip);

      // Select and update cookie
      cookieService.select('intBetslip').setArrayString(cookieBetIdArray);

      // ensure we update state
      fetchBetslipData();
    },
    [betslipUtils, localStorageService, cookieService, fetchBetslipData, oddsFormatIs, stake, setBetslipOpen],
  );

  const removeBetFromBetslip = useCallback(
    (betId: number, disabled?: boolean, bet?: Bet): void => {
      const tempBetslip: Array<any> = [];
      for (let i = 0; i < betslip.length; i++) {
        let betWasRemoved = true;
        if (betslip[i].betId !== betId) {
          betWasRemoved = false;
          tempBetslip.push(betslip[i]);
        }

        if (betWasRemoved) {
          addOrRemoveFromBetslip(
            [{ betId }],
            [betId],
            selectedBookie?.bookmakerCode,
            selectedBookie?.oddsDecimal.toString(),
            bet?.name,
            'betslip',
            bet?.subeventName,
            bet?.marketName,
            'n/a',
          );
        }
      }
      if (!disabled) {
        let bookieExists = false;
        tempBetslip.forEach(bet => {
          bookieExists = bet.odds.some(
            (odds: { bookmakerCode: string }) => odds.bookmakerCode === (selectedBookie?.bookmakerCode ?? ''),
          );
        });

        if (!bookieExists) {
          setSelectedBookie(null);
          fetchBetslipData();
        }
      }

      callbacksRef.current.removeBet?.forEach(callback => callback(betId));
    },
    [addOrRemoveFromBetslip, betslip, selectedBookie, fetchBetslipData],
  );

  const handleClearBetslip = useCallback((): void => {
    // 1. tracking
    const trackingData = {
      event: TRACKING_EVENTS.BET_SLIP_CLEAR,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_CLEAR],
      eventLabel: '',
    };
    trackingActions.eventTracking(trackingData);

    trackingData.event = TRACKING_EVENTS.BET_SLIP_CLOSE;
    trackingData.eventAction = TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_CLOSE];
    trackingActions.eventTracking(trackingData);

    // 2. close betslip and setup callbacks
    shouldResetBetslipOnCloseRef.current = true;
    setBetslipOpen(false);
  }, [setBetslipOpen]);

  useEffect(() => {
    if (open || !shouldResetBetslipOnCloseRef.current) return;
    shouldResetBetslipOnCloseRef.current = false;

    // 1. callbacks
    callbacksRef.current.clearBetslip?.forEach(callback => callback());

    // 2. clear betslip
    const bets = betslipUtils.getBetslipCookieArray();
    const modifiedBets = bets.map(bet => ({ betId: Number(bet[0]) }));
    addOrRemoveFromBetslip(modifiedBets);

    // 3. init state
    setBookieOdds([]);
    setBetslip([]);
  }, [open, betslipUtils, addOrRemoveFromBetslip]);

  const handleClickoutToPlaceBet = useCallback(
    (bookieFromBookieSelector?: BookieOdds): void => {
      try {
        const { getSelectedBookieBetOdds } = betslipUtils;
        const { betIds, betslip, count, selectedBookie: bookieFromSelectedBookie, stake } = betslipState;

        // 0. ensure we handle bookie selector list so
        // users can clickout to non-selected bookie,
        // i.e. those at the bottom of the betslip
        const selectedBookie: BookieOdds = bookieFromBookieSelector || bookieFromSelectedBookie!;
        const bookieCode = selectedBookie?.bookmakerCode;

        // 1. clickout
        const { CLICKOUT_URL, OC_API_KEY } = window.ENV;
        const apiKey = OC_API_KEY[repub as keyof typeof OC_API_KEY];
        const betUrl = `${CLICKOUT_URL}/splash/clickout?betIds=${betIds.join('%2C')}&bookieCode=${encodeURIComponent(bookieCode)}&apiKey=${apiKey}`;
        clickout(betUrl, bookieCode, 'bet', true);

        // 2. tracking
        const genericBetTrackingData: BetTrackingDataGeneric = {
          bookieName: bookmakers[bookieCode]?.bookmakerName,
          bookieCode,
          betStake: stake,
          betOdds: getSelectedBookieBetOdds(selectedBookie, userOddsFormat),
          betAccumulator: count > 1,
          onsite: false,
          // NOTE: custref not available until CI-1167
          custref: undefined,
        };

        const betslipBetsBetTrackingData = betslip.reduce((trackingData, betData) => {
          return {
            ...trackingData,
            marketName: [...(trackingData.marketName || ''), betData.marketName],
            // NOTE: betSport not available until CI-1167
            betSport: undefined,
            betName: [...(trackingData.betName || ''), betData.name],
            subeventName: [...(trackingData.subeventName || ''), betData.subeventName],
            // NOTE: eventName not available until CI-1167
            eventName: undefined,
          };
        }, {} as BetTrackingDataBetslipBets);

        const betTrackingDataAsCSV = Object.entries({
          ...genericBetTrackingData,
          ...betslipBetsBetTrackingData,
        }).reduce((a: Record<string, BetTrackingDataValues>, [key, val]: [string, BetTrackingDataValues]) => {
          a[key] = Array.isArray(val) ? val.join(',') : val;
          return a;
        }, {} as BetTrackingData);

        trackingActions.eventTracking({
          event: TRACKING_EVENTS.BET_PLACED,
          eventCategory: TRACKING_CATEGORIES.BETTING,
          eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_PLACED],
          eventLabel: count === 1 ? 'Place Single Bet' : 'Place Multi Bet',
          ...betTrackingDataAsCSV,
        });
      } catch (error) {
        if (error instanceof Error) {
          console.error(error);
          throw new Error(error.message);
        } else {
          throw error;
        }
      }
    },
    [repub, userOddsFormat, bookmakers, betslipUtils, betslipState],
  );

  const selectBookie = useCallback(
    (idx: number): void => {
      const newSelectedBookie = bookieOdds[idx];
      setSelectedBookie(newSelectedBookie);

      cookieService.select('betslipBookie').set(newSelectedBookie.bookmakerCode, new Date().getTime() + ONE_DAY);

      trackingActions.eventTracking({
        event: TRACKING_EVENTS.CHANGE_BOOKIE_SELECTION,
        eventCategory: TRACKING_CATEGORIES.BETTING,
        eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.CHANGE_BOOKIE_SELECTION],
      });
    },
    [bookieOdds, cookieService],
  );

  // Diagnostic shim for automated tests on non-OC repubs. Gated behind
  // ?cxDebug=1 / cxDebug=1 cookie so it never leaks into production traffic.
  // Intentionally NOT a trigger surface — tests should drive the UI via the
  // ARIA contract documented at the top of packages/features/betslip/src/components/
  // betslip.tsx. This shim only exposes what ARIA can't:
  //   clear()           — teardown (wipes betslip cookies + localStorage + state)
  //   lastFetchStatus() — last /sports-catalogue/v1/betslip outcome, for
  //                       diagnosing why aria-busy never flipped to false.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (repubIs.OC) return; // OC repub owns window.ocBetslip; do not collide
    if (!isCxDebugEnabled()) return;

    window.__cxBetslip = {
      clear: () => {
        cookieService.select('intBetslip').set();
        cookieService.select('betslipBookie').set();
        cookieService.select('betslipStake').set();
        localStorageService.select('intBetslip').set({});
        fetchBetslipData();
      },
      lastFetchStatus: () => lastFetchStatusRef.current,
    };

    return () => {
      delete window.__cxBetslip;
    };
  }, [repubIs.OC, cookieService, localStorageService, fetchBetslipData]);

  const value = useMemo(
    () => ({
      callbacksRef,
      oddsFormatIs,
      userOddsFormat,
      ...betslipUtils,
      ...betslipState,
      toggleAdded,
      toggleBetslip,
      changeStake,
      syncModulesBetslipData,
      fetchBetslipData,
      storeBetslipData,
      handleClearBetslip,
      handleClickoutToPlaceBet,
      addOrRemoveFromBetslip,
      removeBetFromBetslip,
      selectBookie,
    }),
    [
      callbacksRef,
      oddsFormatIs,
      userOddsFormat,
      betslipUtils,
      betslipState,
      toggleAdded,
      toggleBetslip,
      changeStake,
      syncModulesBetslipData,
      fetchBetslipData,
      storeBetslipData,
      handleClearBetslip,
      handleClickoutToPlaceBet,
      addOrRemoveFromBetslip,
      removeBetFromBetslip,
      selectBookie,
    ],
  );

  return (
    <BetslipContext.Provider value={value} {...props}>
      {children}
    </BetslipContext.Provider>
  );
};

export const useBetslipContext = () => {
  const context = useContext(BetslipContext);
  if (!context) {
    throw new Error('useBetslipContext can only be used inside of BetslipProvider');
  }

  return context;
};
